import { useCallback, useState } from 'react'
import type { ApiEndpoint } from 'types/communications'
import type { MutationResult, QueryResult } from 'utils/api'

import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export interface CreateApiEndpointPayload {
  name: string
  url: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  dataPath?: string
  timeoutMs?: number
}

export interface UpdateApiEndpointPayload {
  name?: string
  url?: string
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  dataPath?: string
  timeoutMs?: number
  active?: boolean
}

export function useApiEndpoints(): QueryResult<ApiEndpoint[]> {
  return useApiQuery<ApiEndpoint[]>('/api/api-endpoints')
}

export function useCreateApiEndpoint(): MutationResult<ApiEndpoint, [CreateApiEndpointPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateApiEndpointPayload) => {
      setLoading(true)
      try {
        return await fetchApi<ApiEndpoint>('/api/api-endpoints', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useUpdateApiEndpoint(): MutationResult<ApiEndpoint, [string, UpdateApiEndpointPayload]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateApiEndpointPayload) => {
      setLoading(true)
      try {
        return await fetchApi<ApiEndpoint>(`/api/api-endpoints/${id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}

export function useDeleteApiEndpoint(): MutationResult<void, [string]> {
  const { fetchApi } = useFetchApi()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        await fetchApi(`/api/api-endpoints/${id}`, { method: 'DELETE' })
      } finally {
        setLoading(false)
      }
    },
    [fetchApi],
  )
  return { perform, loading }
}
